import React, { useCallback, useEffect, useState } from "react";
import { Shield, Send, Trash2, Copy, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/auth";

const OPERATOR_ROLES = [
  "intake_specialist",
  "content_operator",
  "ecosystem_operator",
  "reviewer",
  "enhanced_reviewer",
  "read_only_operator",
  "event_operator",
  "relationship_manager",
];

const STATUS_STYLES = {
  PENDING: "bg-amber-100 text-amber-800",
  ACCEPTED: "bg-emerald-100 text-emerald-800",
  REVOKED: "bg-slate-200 text-slate-700",
  EXPIRED: "bg-rose-100 text-rose-800",
};

function acceptUrl(token) {
  const origin = window.location.origin;
  return `${origin}/staff/accept-invitation?token=${encodeURIComponent(token)}`;
}

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [err, setErr] = useState("");
  const [tokens, setTokens] = useState({}); // { invitation_id: raw_token }
  const [copied, setCopied] = useState("");
  const [notice, setNotice] = useState("");

  const [invEmail, setInvEmail] = useState("");
  const [invName, setInvName] = useState("");
  const [invRole, setInvRole] = useState(OPERATOR_ROLES[0]);
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, i] = await Promise.all([
        api.get("/ops/staff"),
        api.get("/ops/staff/invitations"),
      ]);
      setStaff(s.data.items || []);
      setInvitations(i.data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setErr("");
    setNotice("");
    setInviting(true);
    try {
      const { data } = await api.post("/ops/staff/invitations", {
        email: invEmail.trim(),
        role: invRole,
        name: invName.trim() || undefined,
      });
      // Batch 4 — the invitation link is delivered by email only.
      setNotice(`Invitation queued for email delivery to ${data.email}.`);
      setInvEmail("");
      setInvName("");
      setInvRole(OPERATOR_ROLES[0]);
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  const revokeInvitation = async (id) => {
    if (!window.confirm("Revoke this invitation? It cannot be used after revoking.")) return;
    try {
      await api.delete(`/ops/staff/invitations/${id}`);
      setTokens((t) => {
        const n = { ...t };
        delete n[id];
        return n;
      });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const changeStatus = async (userId, action) => {
    if (!window.confirm(`${action === "suspend" ? "Suspend" : "Reactivate"} this operator?`)) return;
    try {
      await api.post(`/ops/staff/${userId}/status`, { action });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const copyLink = async (id) => {
    const raw = tokens[id];
    if (!raw) return;
    try {
      await navigator.clipboard.writeText(acceptUrl(raw));
      setCopied(id);
      setTimeout(() => setCopied(""), 1800);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="space-y-6" data-testid="ops-staff-page">
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-slate-500" />
        <h1 className="text-2xl font-semibold text-slate-900">Staff / Operators</h1>
      </div>
      <p className="text-sm text-slate-600 max-w-3xl">
        Internal only. Staff and operators are managed from this private surface —
        BTC does not expose any public staff CTA or public registration path for
        this role set. Invitations are emailed by BTC Portal to the invited address
        as a single-use link that expires after 72 hours. Emergency revoke remains at{" "}
        <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">
          POST /api/admin/operators/&#123;id&#125;/revoke
        </code>
        .
      </p>
      {notice && (
        <div
          data-testid="ops-staff-invite-notice"
          role="status"
          className="border border-emerald-300 bg-emerald-50 text-emerald-800 text-sm px-4 py-2 rounded"
        >
          {notice}
        </div>
      )}
      {err && (
        <div
          data-testid="ops-staff-error"
          className="border border-rose-300 bg-rose-50 text-rose-800 text-sm px-4 py-2 rounded"
        >
          {err}
        </div>
      )}

      <section
        data-testid="ops-staff-invite-section"
        className="bg-white rounded-xl border p-4 space-y-3"
      >
        <div className="flex items-center gap-2 text-slate-900">
          <Send className="w-4 h-4" /> <span className="font-medium">Invite a staff member</span>
        </div>
        <form
          onSubmit={invite}
          data-testid="ops-staff-invite-form"
          className="grid grid-cols-1 md:grid-cols-4 gap-3"
        >
          <input
            data-testid="ops-staff-invite-email"
            type="email"
            required
            placeholder="email@bondedthrucancer.org"
            value={invEmail}
            onChange={(e) => setInvEmail(e.target.value)}
            className="border rounded px-3 py-2 text-sm md:col-span-1"
          />
          <input
            data-testid="ops-staff-invite-name"
            type="text"
            placeholder="Optional name"
            value={invName}
            onChange={(e) => setInvName(e.target.value)}
            className="border rounded px-3 py-2 text-sm md:col-span-1"
          />
          <select
            data-testid="ops-staff-invite-role"
            value={invRole}
            onChange={(e) => setInvRole(e.target.value)}
            className="border rounded px-3 py-2 text-sm md:col-span-1"
          >
            {OPERATOR_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button
            data-testid="ops-staff-invite-submit"
            disabled={inviting}
            className="bg-slate-900 text-white text-sm rounded px-4 py-2 hover:bg-slate-800 disabled:opacity-50"
          >
            {inviting ? "Sending…" : "Send invitation"}
          </button>
        </form>
        <p className="text-xs text-slate-500">
          Roles listed here are non-privileged operator roles. `admin` and
          `super_admin` are not invitable from this form.
        </p>
      </section>

      <section className="bg-white rounded-xl border overflow-x-auto">
        <div className="px-4 py-3 border-b flex items-center justify-between min-w-[560px]">
          <div className="font-medium text-slate-900">Invitations</div>
          <div className="text-xs text-slate-500">
            {invitations.length} total
          </div>
        </div>
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Expires</th>
              <th className="text-left px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="ops-staff-invitations-body">
            {invitations.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                  No invitations issued yet.
                </td>
              </tr>
            )}
            {invitations.map((inv) => (
              <tr
                key={inv.id}
                className="border-t"
                data-testid={`ops-staff-invitation-row-${inv.id}`}
              >
                <td className="px-4 py-3">{inv.email}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded bg-slate-100 text-xs">
                    {inv.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLES[inv.status] || "bg-slate-100"}`}
                    data-testid={`ops-staff-invitation-status-${inv.id}`}
                  >
                    {inv.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-500">
                  {inv.expires_at?.slice(0, 10)}
                </td>
                <td className="px-4 py-3 space-x-2">
                  {tokens[inv.id] && inv.status === "PENDING" && (
                    <button
                      onClick={() => copyLink(inv.id)}
                      data-testid={`ops-staff-invitation-copy-${inv.id}`}
                      className="inline-flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 rounded px-2 py-1"
                    >
                      {copied === inv.id ? (
                        <>
                          <Check className="w-3 h-3" /> Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" /> Copy link
                        </>
                      )}
                    </button>
                  )}
                  {inv.status === "PENDING" && (
                    <button
                      onClick={() => revokeInvitation(inv.id)}
                      data-testid={`ops-staff-invitation-revoke-${inv.id}`}
                      className="inline-flex items-center gap-1 text-xs text-rose-700 hover:bg-rose-50 rounded px-2 py-1"
                    >
                      <Trash2 className="w-3 h-3" /> Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="bg-white rounded-xl border overflow-x-auto">
        <div className="px-4 py-3 border-b font-medium text-slate-900 min-w-[560px]">
          Active staff & operators
        </div>
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                  No operators registered.
                </td>
              </tr>
            )}
            {staff.map((u) => {
              const suspended = u.is_active === false && !u.revoked_at;
              const revoked = !!u.revoked_at;
              return (
                <tr key={u.id} className="border-t" data-testid={`ops-staff-row-${u.id}`}>
                  <td className="px-4 py-3 text-slate-900">{u.name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-xs">
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {revoked ? (
                      <span className="text-rose-700 font-semibold text-xs">
                        emergency-revoked
                      </span>
                    ) : suspended ? (
                      <span className="text-amber-700 font-semibold text-xs">
                        suspended
                      </span>
                    ) : (
                      <span className="text-emerald-700 font-semibold text-xs">
                        active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 space-x-2">
                    {!revoked && !suspended && (
                      <button
                        onClick={() => changeStatus(u.id, "suspend")}
                        data-testid={`ops-staff-suspend-${u.id}`}
                        className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-800 rounded px-2 py-1"
                      >
                        Suspend
                      </button>
                    )}
                    {!revoked && suspended && (
                      <button
                        onClick={() => changeStatus(u.id, "reactivate")}
                        data-testid={`ops-staff-reactivate-${u.id}`}
                        className="text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded px-2 py-1"
                      >
                        Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
